import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowRight, CheckCircle2, Loader2, XCircle } from 'lucide-react'

import { Button } from '@/components/ui'
import { appRoutes } from '@/constants'
import { getApiErrorMessage } from '@/lib/api-client'
import { useVerifyEmail } from '../hooks'
import { AuthLayout } from './auth-layout'

type VerifyState = 'idle' | 'verifying' | 'success' | 'error'

export function VerifyEmailPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [state, setState] = useState<VerifyState>(token ? 'verifying' : 'idle')
  const mutation = useVerifyEmail()

  useEffect(() => {
    if (token && state === 'verifying') {
      mutation
        .mutateAsync({ token })
        .then(() => {
          setState('success')
          toast.success('Email verified', { description: 'You can now sign in.' })
        })
        .catch((error) => {
          setState('error')
          toast.error(getApiErrorMessage(error, 'Unable to verify email'))
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, state])

  return (
    <AuthLayout
      title={
        state === 'success'
          ? 'Email verified'
          : state === 'error'
            ? 'Verification failed'
            : 'Verifying your email'
      }
      description={
        state === 'success'
          ? 'Your account is verified. You can now sign in.'
          : state === 'error'
            ? 'The verification link is invalid or has expired.'
            : 'Please wait while we confirm your email address.'
      }
    >
      <div className="flex flex-col items-center gap-4 py-2 text-center">
        {state === 'verifying' ? (
          <Loader2 className="size-10 animate-spin text-primary" />
        ) : state === 'success' ? (
          <CheckCircle2 className="size-10 text-success" />
        ) : (
          <XCircle className="size-10 text-destructive" />
        )}

        {state === 'success' ? (
          <Button
            size="lg"
            className="w-full"
            onClick={() => navigate(appRoutes.auth.login)}
          >
            Sign In <ArrowRight className="size-4" />
          </Button>
        ) : null}

        {state === 'error' ? (
          <div className="w-full space-y-2">
            <p className="text-sm text-muted-foreground">
              Request a fresh link or try{' '}
              <Link to={appRoutes.auth.login} className="font-semibold text-primary hover:underline">
                signing in
              </Link>
              .
            </p>
            <Button variant="outline" size="lg" className="w-full" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          </div>
        ) : null}

        {state === 'idle' ? (
          <p className="text-sm text-muted-foreground">
            No verification token provided. Check your email inbox for a verification link, or{' '}
            <Link
              to={appRoutes.auth.verificationPending}
              className="font-semibold text-primary hover:underline"
            >
              request a new link
            </Link>
            .
          </p>
        ) : null}
      </div>
    </AuthLayout>
  )
}